import { useEffect, useState } from 'react'

/*
 * Fortschrittsbalken und Checkliste zusammenbinden.
 *
 * Auf dem Dashboard stehen zwei Darstellungen desselben Ablaufs übereinander:
 * oben der Balken (UI/CompanyProgress) mit einem Knoten je Schritt, darunter
 * die Checkliste (views/common/CompanySteps) mit Text, Frist und Weg dorthin.
 * Wer im Balken „MA hinzufügen“ sah, musste selbst herausfinden, welche der
 * sieben Zeilen darunter dazugehört — und umgekehrt.
 *
 * Deshalb hebt ein Klick auf das eine das andere hervor. Gemerkt wird dabei
 * immer die **Kennung des Balken-Schritts**: Sie ist die gröbere der beiden
 * Listen, mehrere Zeilen der Checkliste können auf denselben Knoten zeigen
 * (vier davon drehen sich um die Mitarbeiter:innen). Andersherum ginge es
 * nicht — welche der vier Zeilen wäre bei einem Klick auf den Knoten gemeint?
 *
 * Die Zuordnung steht hier und nicht in einer der beiden Komponenten: Sonst
 * kennte die eine Seite die Kennungen der anderen, und ein neuer Punkt in der
 * Checkliste würde still ins Leere zeigen.
 */

// Vor der Aktivierung: Voraussetzung (shared/activationRequirements) → Knoten.
const VORAUSSETZUNG_ZU_SCHRITT = {
	// „Unternehmensprofil & Risikoanalyse“ und die Bestätigung der
	// Firmen-E-Mail-Adresse gehören beide zum Unternehmensprofil; im Balken
	// steht dafür ein Knoten.
	riskAnalysis: 'riskAnalysis',
	emailVerified: 'riskAnalysis',
	// Die Bestätigung zur Lebensmittelhygiene (Anforderung #96) steht im
	// Unternehmensprofil unter „Geschäftstätigkeit" — derselbe Knoten.
	hygieneTraining: 'riskAnalysis',

	// Alles, was an den Mitarbeiter:innen hängt: Anzahl, Einverständnis-
	// erklärungen, Warteliste, zugeteilte Funktion.
	employees: 'employees',
	parentalConfirmation: 'employees',
	isInvitedEmployeesCount: 'employees',
	isWorkAreaAssigned: 'employees',

	experts: 'experts',
	businessPlan: 'businessPlan',

	// Das Skills4Retail-Konzept, wo es die Aktivierung sperrt. Steht es
	// stattdessen vor dem ersten Finanzbericht, gehoert es in die zweite Haelfte
	// — dort mit derselben Kennung, siehe unten.
	concept: 'concept'
}

// Nach der Aktivierung führen beide Seiten dieselben Schritte mit denselben
// Kennungen (siehe UI/CompanyProgress/steps.js und CompanySteps).
const SCHRITTE_NACH_AKTIVIERUNG = [
	'shares',
	// Nur vorhanden, wo das Konzept den ersten Finanzbericht sperrt — sonst
	// bauen beide Seiten den Schritt gar nicht erst.
	'concept',
	'reportSemiannual',
	'reportAnnual',
	'invoiceSemiannual',
	'invoiceAnnual',
	'finalReport',
	'certificate'
]

/**
 * Zu welchem Knoten des Balkens gehört eine Zeile der Checkliste?
 *
 * @param id          Kennung der Zeile
 * @param isActivated Unternehmen genehmigt — dann zeigt die Checkliste die
 *                    zweite Hälfte des Ablaufs
 * @returns Kennung des Schritts oder null (kein Gegenstück)
 */
export const progressStepForChecklistItem = (id, isActivated) => {
	if (!id) return null
	if (isActivated) return SCHRITTE_NACH_AKTIVIERUNG.includes(id) ? id : null

	return VORAUSSETZUNG_ZU_SCHRITT[id] || null
}

/**
 * Hat ein Knoten des Balkens ein Gegenstück in der Checkliste?
 *
 * Vor der Aktivierung haben „Hallo“, „Prüfung“ und „Genehmigt“ keines (sie
 * sind nichts zu Erledigendes), nach der Aktivierung keiner der Schritte der
 * Anmeldung. Knoten ohne Gegenstück bleiben unangetastet — ein Klick, der
 * nichts bewirkt, sieht aus wie ein Fehler.
 */
export const hasChecklistCounterpart = (stepId, isActivated) => {
	if (!stepId) return false
	if (isActivated) return SCHRITTE_NACH_AKTIVIERUNG.includes(stepId)

	return Object.values(VORAUSSETZUNG_ZU_SCHRITT).includes(stepId)
}

/**
 * Der hervorgehobene Schritt und wie er sich ändert.
 *
 * Beide Dashboards (Schüler:innen und Lehrkraft) halten diesen Stand für ihre
 * beiden Spalten gemeinsam — er gehört keiner der beiden Komponenten allein.
 */
export const useCompanyStepFocus = () => {
	const [focusId, setFocusId] = useState(null)

	// Noch einmal auf dasselbe: Hervorhebung wieder aus. Ohne das bliebe sie
	// stehen, bis irgendwo anders hingeklickt wird.
	const focusStep = id => setFocusId(vorher => (vorher === id ? null : id))

	// Esc räumt sie weg — dieselbe Taste wie bei den Fenstern
	// (shared/useDialogDismiss).
	useEffect(() => {
		if (!focusId) return undefined

		const beiTaste = evt => {
			if (evt.key === 'Escape') setFocusId(null)
		}

		window.addEventListener('keydown', beiTaste)
		return () => window.removeEventListener('keydown', beiTaste)
	}, [focusId])

	return { focusId, focusStep }
}
